/**
 * The Fullscreen API as every browser has it — or hasn't.
 *
 * Safari only knows the `webkit` spellings, and inside an in-app browser (Messenger, Instagram,
 * Facebook on iOS) not even those: `document.documentElement.requestFullscreen` is undefined, and
 * calling it throws synchronously — a `.catch` on the result never runs because there is no
 * result. The tournament live view asked for fullscreen on load, so every live link opened from
 * Messenger on an iPhone or iPad crashed to the "Something went wrong" screen before showing a
 * score. These helpers never throw: where the API is missing they do nothing.
 */
type AnyDoc = Document & {
    webkitFullscreenElement?: Element | null;
    webkitExitFullscreen?: () => Promise<void> | void;
};
type AnyEl = Element & {
    webkitRequestFullscreen?: () => Promise<void> | void;
};

/** The element in fullscreen, or null — in either spelling. */
export function fullscreenElement(doc: Document = document): Element | null {
    const d = doc as AnyDoc;
    return d.fullscreenElement ?? d.webkitFullscreenElement ?? null;
}

/** Whether this browser can put anything in fullscreen at all. */
export function fullscreenSupported(el: Element = document.documentElement): boolean {
    const e = el as AnyEl;
    return typeof e.requestFullscreen === 'function' || typeof e.webkitRequestFullscreen === 'function';
}

/** Ask for fullscreen; resolves either way, so a refusal or a missing API is never an error. */
export async function requestFullscreen(el: Element = document.documentElement): Promise<void> {
    const e = el as AnyEl;
    try {
        if (typeof e.requestFullscreen === 'function') await e.requestFullscreen();
        else if (typeof e.webkitRequestFullscreen === 'function') await e.webkitRequestFullscreen();
    } catch {
        // Refused (not from a user gesture, or a frame without the permission): stay as we are.
    }
}

export async function exitFullscreen(doc: Document = document): Promise<void> {
    const d = doc as AnyDoc;
    try {
        if (!fullscreenElement(doc)) return;
        if (typeof d.exitFullscreen === 'function') await d.exitFullscreen();
        else if (typeof d.webkitExitFullscreen === 'function') await d.webkitExitFullscreen();
    } catch {
        // nothing to leave
    }
}

/** Leave fullscreen if in it, otherwise enter it. */
export function toggleFullscreen(el: Element = document.documentElement): Promise<void> {
    return fullscreenElement() ? exitFullscreen() : requestFullscreen(el);
}

/** Subscribe to fullscreen changes in both spellings; returns the unsubscribe. */
export function onFullscreenChange(handler: () => void, doc: Document = document): () => void {
    doc.addEventListener('fullscreenchange', handler);
    doc.addEventListener('webkitfullscreenchange', handler);
    return () => {
        doc.removeEventListener('fullscreenchange', handler);
        doc.removeEventListener('webkitfullscreenchange', handler);
    };
}
